import { 
  SORTTER_REDIRECT_URL, 
  SAMBLA_REDIRECT_URL, 
  AXO_REDIRECT_URL,
  AXO_OFFER_ID,
  AXO_AFFILIATE_ID,
  SORTTER_OFFER_ID,
  SORTTER_AFFILIATE_ID,
  SAMBLA_PUBID
} from '@/services/api/constants';
import { FormValues } from '@/types/loan';
import { submitToSortter } from '@/services/api/sortterApi';
import { generateSecureToken, sanitizeString } from '@/utils/security';

// Application providers
export enum ApplicationProvider {
  AXO = 'axo',
  SAMBLA = 'sambla',
  SORTTER = 'sortter'
}

// Status of a provider check
export enum ProviderStatus {
  ACCEPTED = 'accepted',
  DUPLICATE = 'duplicate', 
  ERROR = 'error',
  LOW_QUALITY = 'low_quality',
  PREVIOUS_APPLICANT = 'previous_applicant'
}

// Result of a waterfall check
export interface WaterfallResult {
  provider: ApplicationProvider;
  status: ProviderStatus;
  redirectUrl: string;
  token?: string;
}

/**
 * Process application through provider waterfall with enhanced security
 */
export const processApplicationWaterfall = async (
  token: string | null,
  applicationData?: FormValues
): Promise<WaterfallResult> => {
  console.log('Processing application with token:', token, 'and email:', applicationData?.email);
  
  // Use secure token generation if no token provided
  const safeToken = token || generateSecureToken();
  
  try {
    // Clear cached data if URL contains a reset parameter (for testing)
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('reset_cache') === 'true') {
      console.log('Resetting application cache for testing');
      localStorage.removeItem('recentApplications');
      localStorage.removeItem('redirectHistory');
    }
    
    // Handle explicit provider requests from URL
    const explicitProvider = urlParams.get('provider');
    if (explicitProvider) {
      console.log('Explicit provider request:', explicitProvider);
      
      switch (explicitProvider.toLowerCase()) {
        case 'axo':
          console.log('Explicit request for Axo routing');
          return {
            provider: ApplicationProvider.AXO,
            status: ProviderStatus.ACCEPTED,
            redirectUrl: getAxoRedirectUrl(safeToken),
            token: safeToken
          };
        case 'sambla':
          console.log('Explicit request for Sambla routing');
          return {
            provider: ApplicationProvider.SAMBLA,
            status: ProviderStatus.ACCEPTED,
            redirectUrl: getSamblaRedirectUrl(),
            token: safeToken
          };
        case 'sortter':
          console.log('Explicit request for Sortter routing');
          return {
            provider: ApplicationProvider.SORTTER,
            status: ProviderStatus.ACCEPTED,
            redirectUrl: getSortterRedirectUrl(),
            token: safeToken
          };
      }
    }
    
    // Check if email has been used within the last 30 days
    if (applicationData?.email) {
      const hasRecentApplication = await checkRecentApplicationWithin30Days(applicationData.email);
      const redirectHistory = await getRedirectHistory(applicationData.email);
      console.log(`User email ${applicationData.email} has recent application: ${hasRecentApplication}`);
      console.log(`Redirect history: ${JSON.stringify(redirectHistory)}`);
      
      // Determine which provider to use based on history
      if (!hasRecentApplication) {
        // First-time user or user who hasn't applied in 30+ days
        console.log('First application attempt or no recent applications - routing to AXO');
        storeRedirectInfo(applicationData.email, ApplicationProvider.AXO);
        storeApplicationInfo(applicationData.email);
        
        return {
          provider: ApplicationProvider.AXO,
          status: ProviderStatus.ACCEPTED,
          redirectUrl: getAxoRedirectUrl(safeToken),
          token: safeToken
        };
      } else {
        // User has applied within the last 30 days - check which providers they've used
        if (!redirectHistory.includes(ApplicationProvider.AXO)) {
          console.log('Recent applicant but no AXO history - routing to AXO');
          storeRedirectInfo(applicationData.email, ApplicationProvider.AXO);
          
          return {
            provider: ApplicationProvider.AXO,
            status: ProviderStatus.ACCEPTED,
            redirectUrl: getAxoRedirectUrl(safeToken),
            token: safeToken
          };
        } else if (!redirectHistory.includes(ApplicationProvider.SAMBLA)) {
          console.log('Recent applicant who has used AXO - routing to Sambla');
          storeRedirectInfo(applicationData.email, ApplicationProvider.SAMBLA);
          
          return {
            provider: ApplicationProvider.SAMBLA,
            status: ProviderStatus.ACCEPTED,
            redirectUrl: getSamblaRedirectUrl(),
            token: safeToken
          };
        } else if (!redirectHistory.includes(ApplicationProvider.SORTTER)) {
          console.log('Recent applicant who has used AXO and Sambla - routing to Sortter');
          storeRedirectInfo(applicationData.email, ApplicationProvider.SORTTER);
          
          // Submit to Sortter API to get a real token
          if (applicationData) {
            try {
              console.log("Submitting application to Sortter API");
              const sortterToken = await submitToSortter(applicationData);
              
              if (sortterToken) {
                console.log("Received token from Sortter API:", sortterToken);
                return {
                  provider: ApplicationProvider.SORTTER,
                  status: ProviderStatus.ACCEPTED,
                  redirectUrl: getSortterRedirectUrl(),
                  token: sortterToken
                };
              }
            } catch (error) {
              console.error("Error submitting to Sortter API:", error);
            }
          }
          
          // Fallback to Sortter with generated token
          return {
            provider: ApplicationProvider.SORTTER,
            status: ProviderStatus.ACCEPTED,
            redirectUrl: getSortterRedirectUrl(),
            token: safeToken
          };
        } else {
          // They've used all providers, default back to AXO
          console.log('Recent applicant who has used all providers - routing back to AXO');
          storeRedirectInfo(applicationData.email, ApplicationProvider.AXO);
          
          return {
            provider: ApplicationProvider.AXO,
            status: ProviderStatus.PREVIOUS_APPLICANT,
            redirectUrl: getAxoRedirectUrl(safeToken),
            token: safeToken
          };
        }
      }
    }
    
    // If this is a new application and email is provided, store it
    if (applicationData?.email) {
      storeApplicationInfo(applicationData.email);
      storeRedirectInfo(applicationData.email, ApplicationProvider.AXO);
    }
    
    // Default case: Always route to AXO first
    console.log('Default case - routing to AXO (primary provider)');
    
    return {
      provider: ApplicationProvider.AXO,
      status: ProviderStatus.ACCEPTED,
      redirectUrl: getAxoRedirectUrl(safeToken),
      token: safeToken
    };
    
  } catch (error) {
    console.error('Error in application waterfall processing:', error);
    
    // Even if processing fails, default to AXO
    return {
      provider: ApplicationProvider.AXO,
      status: ProviderStatus.ERROR,
      redirectUrl: getAxoRedirectUrl(safeToken),
      token: safeToken
    };
  }
};

/**
 * Check if user has applied within the last 30 days
 */
const checkRecentApplicationWithin30Days = async (email: string): Promise<boolean> => {
  try {
    // In production, this would check a database
    // For demonstration, we'll use localStorage
    const recentApplications = localStorage.getItem('recentApplications');
    if (!recentApplications) return false;
    
    const applications = JSON.parse(recentApplications);
    const userApplication = applications[email];
    
    if (!userApplication) return false;
    
    // Check if application is within last 30 days
    const applicationDate = new Date(userApplication.timestamp);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    console.log(`Last application: ${applicationDate}, Thirty days ago: ${thirtyDaysAgo}`);
    console.log(`Is within 30 days? ${applicationDate > thirtyDaysAgo}`);
    
    return applicationDate > thirtyDaysAgo;
  } catch (error) {
    console.error('Error checking recent applications:', error);
    return false;
  }
};

/**
 * Get the number of applications a user has made - improved version that properly counts
 * applications from redirect history
 */
const getApplicationCountFromStorage = async (email: string): Promise<number> => {
  try {
    // Get redirect history from localStorage
    const redirectHistory = localStorage.getItem('redirectHistory');
    if (!redirectHistory) return 1; // First application if no history
    
    const redirects = JSON.parse(redirectHistory);
    const userRedirects = redirects[email];
    
    if (!userRedirects || userRedirects.length === 0) return 1;
    
    // Get unique providers user has been redirected to in the past
    // This is crucial for determining if we're on 1st, 2nd, 3rd application
    const providers = new Set();
    userRedirects.forEach((redirect: any) => {
      providers.add(redirect.provider);
    });
    
    console.log("Email", email, "has been redirected to these providers:", Array.from(providers));
    
    // Return the count of unique providers + 1 for the current application
    return providers.size + 1;
  } catch (error) {
    console.error('Error in getApplicationCountFromStorage:', error);
    return 1; // Default to first application on error
  }
};

/**
 * Get the number of applications a user has made
 * @deprecated Use getApplicationCountFromStorage instead for more accurate counts
 */
const getApplicationCount = async (email: string): Promise<number> => {
  try {
    // In production, this would check a database
    // For demonstration, we'll use localStorage
    const recentApplications = localStorage.getItem('recentApplications');
    if (!recentApplications) return 1; // Assume first application if no data
    
    const applications = JSON.parse(recentApplications);
    const userApplication = applications[email];
    
    if (!userApplication) return 1; // Assume first application if no user data
    
    // Check application count from redirect history
    const redirectHistory = localStorage.getItem('redirectHistory');
    if (!redirectHistory) return 1;
    
    const redirects = JSON.parse(redirectHistory);
    const userRedirects = redirects[email];
    
    if (!userRedirects) return 1;
    
    // Count unique applications within the last 2 weeks
    const twoWeeksAgo = new Date();
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
    
    const recentRedirects = userRedirects.filter((redirect: any) => 
      new Date(redirect.timestamp) > twoWeeksAgo
    );
    
    // Get unique providers used
    const uniqueProviders = new Set(recentRedirects.map((r: any) => r.provider));
    
    // Return count of unique providers plus 1 for current application
    return uniqueProviders.size + 1;
  } catch (error) {
    console.error('Error checking application count:', error);
    return 1; // Default to first application on error
  }
};

/**
 * Get redirect history for a user
 */
const getRedirectHistory = async (email: string): Promise<ApplicationProvider[]> => {
  try {
    const redirectHistory = localStorage.getItem('redirectHistory');
    if (!redirectHistory) return [];
    
    const redirects = JSON.parse(redirectHistory);
    const userRedirects = redirects[email];
    
    if (!userRedirects) return [];
    
    // Get list of providers user has been redirected to
    return userRedirects.map((r: any) => r.provider);
  } catch (error) {
    console.error('Error getting redirect history:', error);
    return [];
  }
};

/**
 * Check if user has applied within the last 2 weeks
 */
const checkRecentApplication = async (email: string): Promise<boolean> => {
  try {
    // In production, this would check a database
    // For demonstration, we'll use localStorage
    const recentApplications = localStorage.getItem('recentApplications');
    if (!recentApplications) return false;
    
    const applications = JSON.parse(recentApplications);
    const userApplication = applications[email];
    
    if (!userApplication) return false;
    
    // Check if application is within last 2 weeks
    const applicationDate = new Date(userApplication.timestamp);
    const twoWeeksAgo = new Date();
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
    
    console.log(`Last application: ${applicationDate}, Two weeks ago: ${twoWeeksAgo}`);
    console.log(`Is recent? ${applicationDate > twoWeeksAgo}`);
    
    return applicationDate > twoWeeksAgo;
  } catch (error) {
    console.error('Error checking recent applications:', error);
    return false;
  }
};

/**
 * Check if user has been redirected to Sambla within the last 2 weeks
 */
const checkRecentSamblaRedirect = async (email: string): Promise<boolean> => {
  try {
    // In production, this would check a database
    // For demonstration, we'll use localStorage
    const redirectHistory = localStorage.getItem('redirectHistory');
    if (!redirectHistory) return false;
    
    const redirects = JSON.parse(redirectHistory);
    const userRedirects = redirects[email];
    
    if (!userRedirects) return false;
    
    // Find any Sambla redirects
    const samblaRedirects = userRedirects.filter((redirect: any) => 
      redirect.provider === ApplicationProvider.SAMBLA
    );
    
    if (samblaRedirects.length === 0) return false;
    
    // Check if most recent redirect is within last 2 weeks
    const latestRedirect = samblaRedirects.sort((a: any, b: any) => 
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    )[0];
    
    const redirectDate = new Date(latestRedirect.timestamp);
    const twoWeeksAgo = new Date();
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
    
    console.log(`Last Sambla redirect: ${redirectDate}, Two weeks ago: ${twoWeeksAgo}`);
    console.log(`Is recent? ${redirectDate > twoWeeksAgo}`);
    
    return redirectDate > twoWeeksAgo;
  } catch (error) {
    console.error('Error checking recent Sambla redirects:', error);
    return false;
  }
};

/**
 * Check if user has been redirected to Axo within the last 2 weeks
 */
const checkRecentAxoRedirect = async (email: string): Promise<boolean> => {
  try {
    // In production, this would check a database
    // For demonstration, we'll use localStorage
    const redirectHistory = localStorage.getItem('redirectHistory');
    if (!redirectHistory) return false;
    
    const redirects = JSON.parse(redirectHistory);
    const userRedirects = redirects[email];
    
    if (!userRedirects) return false;
    
    // Find any Axo redirects
    const axoRedirects = userRedirects.filter((redirect: any) => 
      redirect.provider === ApplicationProvider.AXO
    );
    
    if (axoRedirects.length === 0) return false;
    
    // Check if most recent redirect is within last 2 weeks
    const latestRedirect = axoRedirects.sort((a: any, b: any) => 
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    )[0];
    
    const redirectDate = new Date(latestRedirect.timestamp);
    const twoWeeksAgo = new Date();
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
    
    console.log(`Last Axo redirect: ${redirectDate}, Two weeks ago: ${twoWeeksAgo}`);
    console.log(`Is recent? ${redirectDate > twoWeeksAgo}`);
    
    return redirectDate > twoWeeksAgo;
  } catch (error) {
    console.error('Error checking recent Axo redirects:', error);
    return false;
  }
};

/**
 * Store information about a new loan application
 */
const storeApplicationInfo = (email: string): void => {
  try {
    // In production, this would store to a database
    // For demonstration, we'll use localStorage
    const recentApplications = localStorage.getItem('recentApplications') || '{}';
    const applications = JSON.parse(recentApplications);
    
    // Fix for same email issue - ensure we're tracking by email with current timestamp
    applications[email] = {
      timestamp: new Date().toISOString(),
      applied: true
    };
    
    localStorage.setItem('recentApplications', JSON.stringify(applications));
    console.log('Stored application info for email:', email);
  } catch (error) {
    console.error('Error storing application info:', error);
  }
};

/**
 * Store information about a redirect
 */
const storeRedirectInfo = (email: string, provider: ApplicationProvider): void => {
  try {
    // In production, this would store to a database
    // For demonstration, we'll use localStorage
    const redirectHistory = localStorage.getItem('redirectHistory') || '{}';
    const redirects = JSON.parse(redirectHistory);
    
    if (!redirects[email]) {
      redirects[email] = [];
    }
    
    redirects[email].push({
      provider,
      timestamp: new Date().toISOString()
    });
    
    localStorage.setItem('redirectHistory', JSON.stringify(redirects));
    console.log('Stored redirect info for email:', email, 'to provider:', provider);
  } catch (error) {
    console.error('Error storing redirect info:', error);
  }
};

/**
 * Get the Axo redirect URL with enhanced security
 */
export const getAxoRedirectUrl = (token?: string): string => {
  try {
    const baseUrl = AXO_REDIRECT_URL;
    const url = new URL(baseUrl);
    
    // Add the required parameters with validation
    const offerId = sanitizeString(AXO_OFFER_ID);
    const affId = sanitizeString(AXO_AFFILIATE_ID);
    
    url.searchParams.set('offer_id', offerId);
    url.searchParams.set('aff_id', affId);
    
    // Add token as aff_sub parameter if provided
    if (token) {
      const sanitizedToken = sanitizeString(token);
      url.searchParams.set('aff_sub', sanitizedToken);
    }
    
    console.log("Generated Axo redirect URL:", url.toString());
    return url.toString();
  } catch (error) {
    console.error("Error creating Axo URL:", error);
    // Fallback URL construction with sanitization
    const sanitizedOfferId = sanitizeString(AXO_OFFER_ID);
    const sanitizedAffId = sanitizeString(AXO_AFFILIATE_ID);
    const sanitizedToken = token ? sanitizeString(token) : '';
    
    return `${AXO_REDIRECT_URL}?offer_id=${sanitizedOfferId}&aff_id=${sanitizedAffId}${sanitizedToken ? '&aff_sub=' + encodeURIComponent(sanitizedToken) : ''}`;
  }
};

/**
 * Get the Sambla redirect URL with enhanced security
 */
export const getSamblaRedirectUrl = (): string => {
  try {
    const baseUrl = SAMBLA_REDIRECT_URL;
    const url = new URL(baseUrl);
    
    // Add the pubid parameter with validation
    const pubId = sanitizeString(SAMBLA_PUBID);
    url.searchParams.set('pubid', pubId);
    
    console.log("Generated Sambla redirect URL:", url.toString());
    return url.toString();
  } catch (error) {
    console.error("Error creating Sambla URL:", error);
    // Fallback URL construction
    const sanitizedPubId = sanitizeString(SAMBLA_PUBID);
    return `${SAMBLA_REDIRECT_URL}?pubid=${sanitizedPubId}`;
  }
};

/**
 * Get the Sortter redirect URL with enhanced security
 */
export const getSortterRedirectUrl = (_token?: string): string => {
  // Return exact URL as specified, with validation
  const sanitizedOfferId = sanitizeString(SORTTER_OFFER_ID);
  const sanitizedAffId = sanitizeString(SORTTER_AFFILIATE_ID);
  
  return `https://sortter.scaletrk.com/click?o=${sanitizedOfferId}&a=${sanitizedAffId}`;
};

/**
 * Function to generate postback URL for Axo with tracking parameters
 * Used for conversion tracking
 */
export const generateAxoPostbackUrl = (transactionId: string, options: {
  amount?: number;
  status?: string;
  userId?: string;
  source?: string;
} = {}): string => {
  // Base URL for Axo postbacks
  const baseUrl = 'https://go.axolaina.fi/aff_lsr';
  
  try {
    const url = new URL(baseUrl);
    
    // Required parameters
    url.searchParams.set('offer_id', AXO_OFFER_ID);
    url.searchParams.set('aff_id', AXO_AFFILIATE_ID);
    
    // Transaction ID is required for tracking
    url.searchParams.set('transaction_id', transactionId);
    
    // Optional parameters
    if (options.amount) {
      url.searchParams.set('sale_amount', options.amount.toString());
    }
    
    if (options.status) {
      url.searchParams.set('adv_sub', options.status);
    }
    
    if (options.userId) {
      url.searchParams.set('aff_sub', options.userId);
    }
    
    if (options.source) {
      url.searchParams.set('source', options.source);
    }
    
    return url.toString();
  } catch (error) {
    console.error("Error creating Axo postback URL:", error);
    return `${baseUrl}?offer_id=${AXO_OFFER_ID}&aff_id=${AXO_AFFILIATE_ID}&transaction_id=${transactionId}`;
  }
};
